// frontend/src/App.jsx
import React from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Header from "./components/Header/Header";
import HeaderMoveControl from "./components/HeaderMoveControl/HeaderMoveControl";
import Footer from "./components/Footer/Footer";
import HomePage from "./components/HomePage/HomePage";
import DiabetesBefundForm from "./components/DiabetesBefundForm/DiabetesBefundForm";
import Diagnose from "./components/Diagnose/Diagnose";
import AllgemeineDaten from "./components/AllgemeineDaten/AllgemeineDaten";
import KlinischerBefund from "./components/KlinischerBefund/KlinischerBefund";
import FootStatusForm from "./components/Fussstatus/Fussstatus";
import Fussstatus from "./components/Fussstatus/Fussstatus";
import FussstatusGrafik from "./components/FussstatusGrafik/FussstatusGrafik";
import Kriterien from "./components/Kriterien/Kriterien";
import Kategorisierung from "./components/Kategorisierung/kategorisierung";
import Abschluss from "./components/Abschluss/Abschluss";
import Schuhversorgung from "./components/Schuhversorgung/schuhversorgung";
import Login from "./components/Login/Login";
import Patientenliste from "./components/Patientenliste/Patientenliste";
import Orthotimer from "./components/Orthotimer/Orthotimer";
import MosPublicPage from "./components/MosPublicPage/MosPublicPage";
import MosLinks from "./components/MosLinks/MosLinks";
import MosPatientenListe from "./components/MosPatientenListe/MosPatientenListe";
import MosDashboard from "./components/MosDashboard/MosDashboard";
import OsseDashboard from "./components/OsseDashboard/OsseDashboard";

// Providers
import { AllgemeineDatenProvider } from "./context/AllgemeineDatenContext";
import { KlinischerBefundProvider } from "./context/KlinischerBefundContext";
import { FussstatusProvider } from "./context/FussstatusContext";
import { KriterienProvider } from "./context/KriterienContext";
import { KategorisierungProvider } from "./context/KategorisierungContext";
import { SchuhversorgungProvider } from "./context/SchuhversorgungContext";
import { BefundBogenProvider } from "./context/BefundbogenContext";
import { FussstatusGrafikProvider } from "./context/FussstatusGrafikContext";
import { OrthotimgerProvider } from "./context/OrthotimgerContext";
import { MOSFragebogenAdminProvider, MOSFragebogenPublicProvider } from "./context/MOSFragebogenContext";

// ProtectedRoute
import ProtectedRoute from "./utils/ProtectedRoute";

function Layout() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Routes with no header
  const noHeaderRoutes = ["/login", "/"];
  const isMosPublic = location.pathname.startsWith("/mos/");
  // Normal header routes
  const normalHeaderRoutes = ["/start", "/diabetesBefundForm", "/bewertung"];

  const useNoHeader = noHeaderRoutes.includes(location.pathname) || isMosPublic;
  const useNormalHeader = normalHeaderRoutes.includes(location.pathname);

  const Providers = ({ children }) =>
    isAuthenticated ? (
      <BefundBogenProvider>
        <AllgemeineDatenProvider>
          <KlinischerBefundProvider>
            <FussstatusProvider>
              <KriterienProvider>
                <KategorisierungProvider>
                  <SchuhversorgungProvider>
                    <FussstatusGrafikProvider>
                    <OrthotimgerProvider>
                      <MOSFragebogenAdminProvider>{children}</MOSFragebogenAdminProvider>
                    </OrthotimgerProvider>
                  </FussstatusGrafikProvider>
                  </SchuhversorgungProvider>
                </KategorisierungProvider>
              </KriterienProvider>
            </FussstatusProvider>
          </KlinischerBefundProvider>
        </AllgemeineDatenProvider>
      </BefundBogenProvider>
    ) : (
      <MOSFragebogenPublicProvider>{children}</MOSFragebogenPublicProvider>
    );

  return (
    <Providers>
      {!useNoHeader && (useNormalHeader ? <Header /> : <HeaderMoveControl />)}
      
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/mos/:token" element={<MosPublicPage />} />

        {/* Protected Routes */}
        <Route
          path="/start"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/diabetesBefundForm"
          element={
            <ProtectedRoute>
              <DiabetesBefundForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bewertung"
          element={
            <ProtectedRoute>
              <Diagnose />
            </ProtectedRoute>
          }
        />
        <Route
          path="/patientenliste"
          element={
            <ProtectedRoute>
              <Patientenliste />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allgemeineDaten"
          element={
            <ProtectedRoute>
              <AllgemeineDaten />
            </ProtectedRoute>
          }
        />
        <Route
          path="/klinischerBefund"
          element={
            <ProtectedRoute>
              <KlinischerBefund />
            </ProtectedRoute>
          }
        />
        <Route
          path="/footStatusForm"
          element={
            <ProtectedRoute>
              <FootStatusForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fussstatus"
          element={
            <ProtectedRoute>
              <Fussstatus />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fussstatusGrafik"
          element={
            <ProtectedRoute>
              <FussstatusGrafik />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kriterien"
          element={
            <ProtectedRoute>
              <Kriterien />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kategorisierung"
          element={
            <ProtectedRoute>
              <Kategorisierung />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schuhversorgung"
          element={
            <ProtectedRoute>
              <Schuhversorgung />
            </ProtectedRoute>
          }
        />
        <Route
          path="/abschluss"
          element={
            <ProtectedRoute>
              <Abschluss />
            </ProtectedRoute>
          }
        />

        <Route
          path="/orthotimer"
          element={
            <ProtectedRoute>
              <Orthotimer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mos-links"
          element={
            <ProtectedRoute>
              <MosLinks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mos-patienten"
          element={
            <ProtectedRoute>
              <MosPatientenListe />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mos-dashboard"
          element={
            <ProtectedRoute>
              <MosDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/osse-dashboard"
          element={
            <ProtectedRoute>
              <OsseDashboard />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {!isMosPublic && <Footer />}
    </Providers>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;